<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <admin-breadcrumb :items="[{ text: 'Taxonomies', path: {name: 'admin-taxonomies'} }]" name="Category Types" no-title/>
    <b-row>
      <b-col cols="12">
        <admin-panel title="Category Types">
          <template v-slot:header-right>
            <no-permission-wrapper :show="!canCreateTaxonomies">
              <b-btn variant="success" @click="openNewModal" :disabled="!canCreateTaxonomies">Add new Category Type</b-btn>
            </no-permission-wrapper>
          </template>
          <b-table
              :fields="table_fields"
              :items="categoryTypes"
              table-class="text-nowrap"
              responsive
          >
            <template #cell(name)="data">
              <h4 class="mb-0">{{ data.item.name }}</h4>
            </template>
            <template #cell(productionMethod)="data">
              <b-badge v-if="data.item.is_grow" variant="success">Grow</b-badge>
              <b-badge v-if="data.item.is_manufacture" variant="secondary">Manufacture</b-badge>
            </template>
            <template #cell(actions)="data">
              <no-permission-wrapper :show="!canUpdateTaxonomies">
                <b-btn variant="primary" @click.prevent="openEditModal(data.item)" :disabled="!canUpdateTaxonomies">Edit</b-btn>
              </no-permission-wrapper>
              <no-permission-wrapper :show="!canDeleteTaxonomies">
                <b-btn variant="danger" @click.prevent="confirmDelete(data.item)" :disabled="!canDeleteTaxonomies">Delete</b-btn>
              </no-permission-wrapper>
            </template>
            <template #cell(order)="data">
              <no-permission-wrapper :show="!canUpdateTaxonomies">
                <b-button v-if="data.index > 0" @click="changeOrder(data.index, data.index - 1)" :disabled="!canUpdateTaxonomies">
                  <b-icon-arrow-up/>
                </b-button>
                <b-button v-if="data.index < categoryTypes.length - 1" @click="changeOrder(data.index, data.index + 1)" :disabled="!canUpdateTaxonomies">
                  <b-icon-arrow-down/>
                </b-button>
              </no-permission-wrapper>
            </template>
          </b-table>
        </admin-panel>
      </b-col>
    </b-row>
    <admin-category-type-edit-modal
        v-if="edit_category_type"
        :category-type="edit_category_type"
        @cancel="edit_category_type = null"
        @success="onCategoryTypeEdited"
        visible
    />
    <admin-category-type-add-modal
      ref="newModal"
      @success="onCategoryTypeAdded"
    />
  </b-container>
</template>

<script>
import AdminPanel from "../components/AdminPanel";
import {API_ROUTES} from "../api";
import AdminCategoryTypeEditModal from "../components/AdminCategoryTypeEditModal";
import AdminCategoryTypeAddModal from "../components/AdminCategoryTypeAddModal";
import AdminPreloader from "../components/AdminPreloader";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import NoPermissionWrapper from "../components/NoPermissionWrapper.vue";

export default {
  name: "AdminCategoryTypes",
  components: {
    NoPermissionWrapper,
    AdminBreadcrumb, AdminPreloader, AdminCategoryTypeAddModal, AdminCategoryTypeEditModal, AdminPanel},
  data() {
    return {
      loading: false,
      table_fields: [
        {
          key: "order",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "name",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "productionMethod",
          label: "Production Method",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "actions",
          thClass: "border-top-0",
          tdClass: "align-middle"
        }
      ],
      edit_category_type: null
    }
  },
  computed: {
    canCreateTaxonomies() {
      return this.$store.getters['auth/hasAdminPermissions']('taxonomy_create');
    },
    canUpdateTaxonomies() {
      return this.$store.getters['auth/hasAdminPermissions']('taxonomy_update');
    },
    canDeleteTaxonomies() {
      return this.$store.getters['auth/hasAdminPermissions']('taxonomy_delete');
    },
    search() {
      return this.$store.state.current.search;
    },
    categoryTypes() {
      let categoryTypes = this.$store.state.data.category_types;
      if (this.search) {
        let search = this.search.toLowerCase();
        categoryTypes = categoryTypes.filter(ct => ct.name.toLowerCase().indexOf(search) >= 0);
      }

      return categoryTypes;
    }
  },
  methods: {
    checkPermission(permission) {
      if (!this[permission]) {
        this.$bvToast.toast('Sorry! You don\'t have permission to do this action', {
          title: 'Denied!',
          variant: 'danger'
        });
        return false;
      }
      return true;
    },
    openNewModal() {
      if (!this.checkPermission('canCreateTaxonomies')) {
        return;
      }
      this.$refs.newModal.open();
    },
    openEditModal(categoryType) {
      if (!this.checkPermission('canUpdateTaxonomies')) {
        return;
      }
      this.edit_category_type = categoryType;
    },
    confirmDelete(categoryType) {
      if (!this.checkPermission('canDeleteTaxonomies')) {
        return;
      }
      this.$bvModal.msgBoxConfirm(`You are going to delete ${categoryType.name} category type`, {
        title: 'Please confirm',
        okVariant: 'success',
        centered: true
      }).then(value => {
        if (value) {
          this.loading = true;
          this.$api.post(`${API_ROUTES.ADMIN_TAXONOMY_CATEGORY_TYPES}/${categoryType.id}/delete`).then(() => {
            let index = this.categoryTypes.findIndex(ct => ct.id === categoryType.id);
            if (index >= 0) {
              let categoryTypes = [...this.categoryTypes];
              categoryTypes.splice(index, 1);
              this.$store.commit('data/setCategoryTypes', categoryTypes);
            }
            this.$bvToast.toast('Category type has been successfully deleted', {
              title: 'Success!',
              variant: 'success'
            });
            this.formValidation = {};
          }).catch(({response: {data}}) => {
            this.$bvToast.toast(data.message ?? 'Failed to process operation', {
              title: 'Error!',
              variant: 'danger'
            });
          }).finally(() => {
            this.loading = false;
          });
        }
      });
    },
    onCategoryTypeAdded(categoryType) {
      this.$bvToast.toast('Category type has been successfully created', {
        title: 'Success!',
        variant: 'success'
      });
      let categoryTypes = [...this.categoryTypes];
      categoryTypes.push(categoryType);
      this.$store.commit('data/setCategoryTypes', categoryTypes);
    },
    onCategoryTypeEdited(categoryType) {
      this.$bvToast.toast('Category type has been successfully updated', {
        title: 'Success!',
        variant: 'success'
      });
      let categoryTypes = [...this.categoryTypes];
      let index = categoryTypes.findIndex(ct => ct.id === categoryType.id);
      if (index >= 0) {
        categoryTypes[index] = categoryType;
      } else {
        categoryTypes.push(categoryType);
      }
      this.$store.commit('data/setCategoryTypes', categoryTypes);
    },
    changeOrder(indexOne, indexTwo) {
      let itemOne = this.categoryTypes[indexOne],
          itemTwo = this.categoryTypes[indexTwo];
      if (itemOne && itemTwo) {
        this.loading = true;
        this.$api.post(`${API_ROUTES.ADMIN_TAXONOMY_CATEGORY_TYPES}/change-order`, {
          first: itemOne.id,
          second: itemTwo.id
        }).then(({data}) => {
          this.$store.commit('data/setCategoryTypes', data.category_types);
        }).catch(() => {

        }).finally(() => {
          this.loading = false;
        })
      }
    }
  }
}
</script>
